import { faKey } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Dialog, Input } from "@scribenote/brian-design-system";
import { useResetPin } from "@users/userQueries";
import { useState } from "react";

const PIN_PATTERN = /^\d{4}$/;

export const ResetPinButton = ({
  user,
}: {
  user: { id: string; displayName: string };
}) => {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const resetPin = useResetPin();

  const close = () => {
    resetPin.reset();
    setPin("");
    setOpen(false);
  };

  const isValid = PIN_PATTERN.test(pin);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid={`admin-user-reset-pin-${user.id}`}
      >
        <FontAwesomeIcon icon={faKey} aria-hidden="true" />
        Reset PIN
      </Button>

      <Dialog
        open={open}
        onClose={close}
        size="sm"
        /* A reset already in flight has nothing to go back to. */
        dismissable={!resetPin.isPending}
        title={
          // Dialog takes no testid; dropped while closed since a native <dialog> stays mounted.
          <span
            data-testid={
              open ? `admin-user-reset-pin-modal-${user.id}` : undefined
            }
          >
            Set a new PIN for {user.displayName}
          </span>
        }
        footer={
          <>
            <Button
              variant="outline"
              onClick={close}
              disabled={resetPin.isPending}
              data-testid={`admin-user-reset-pin-cancel-${user.id}`}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                resetPin.mutate({ userId: user.id, pin }, { onSuccess: close })
              }
              disabled={!isValid}
              busy={resetPin.isPending}
              data-testid={`admin-user-reset-pin-submit-${user.id}`}
            >
              {resetPin.isPending ? "Saving" : "Set PIN"}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-annotation">
            Their current PIN stops working immediately. This can&apos;t be
            undone.
          </p>
          <Input
            label="New PIN"
            value={pin}
            onChange={(e) =>
              setPin(e.target.value.replace(/\D/g, "").slice(0, 4))
            }
            inputMode="numeric"
            placeholder="4 digits"
            disabled={resetPin.isPending}
            error={
              resetPin.isError ? "Couldn’t set the PIN. Try again." : undefined
            }
            data-testid={`admin-user-reset-pin-input-${user.id}`}
          />
        </div>
      </Dialog>
    </>
  );
};
